import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Snackbar,
    Divider,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localizedFormat from 'dayjs/plugin/localizedFormat';


import StakeholderService from "../services/StakeholderService";
import ActionService from "../services/ActionService";
import TableDataService from "../services/TableService";

dayjs.extend(weekday);
dayjs.extend(localizedFormat);
dayjs.locale('it');
/* global NDEFReader */

const isWebNFCSupported = 'NDEFReader' in window;

const NFCScanner = () => {
    const [movementType, setMovementType] = useState(null);
    const [activityType, setActivityType] = useState('');
    const [scanMessage, setScanMessage] = useState(null);
    const [scanSeverity, setScanSeverity] = useState('info');
    const [scanning, setScanning] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [tables, setTables] = useState([]);
    const [nfcError, setNfcError] = useState(null);

    const [currentTime, setCurrentTime] = useState(dayjs().format('HH:mm:ss'));
    const [currentDateFormatted, setCurrentDateFormatted] = useState(dayjs().format('dddd D MMMM YYYY'));

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(dayjs().format('HH:mm:ss'));
            setCurrentDateFormatted(dayjs().format('dddd D MMMM YYYY'));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        TableDataService.getAll()
            .then(response => {
                const activityTypes = response.data.filter(item => item.riferimento === 7);
                setTables(activityTypes);
            })
            .catch(e => {
                console.error("Errore nel recupero dei tipi di attività:", e);
                setNfcError("Impossibile caricare i tipi di attività.");
            });
    }, []);

    const startNFCScan = useCallback(async () => {
        if (!isWebNFCSupported) {
            setNfcError("Il tuo browser/dispositivo non supporta l'API Web NFC.");
            return;
        }

        if (!movementType || !activityType) {
            setScanMessage("Per favore, seleziona 'Entrata/Uscita' e 'Tipo attività' prima di scansionare.");
            setScanSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        setScanning(true);
        setScanMessage("Avvicina la card al lettore NFC...");
        setScanSeverity('info');
        setSnackbarOpen(true);
        setNfcError(null);

        try {
            const reader = window.NDEFReader ? new window.NDEFReader() : null;

            if (!reader) {
                throw new Error("NDEFReader non disponibile nonostante il check iniziale.");
            }

            await reader.scan();
            console.log("Scansione NFC avviata.");

            reader.onreading = async ({ message, serialNumber }) => {
                setScanning(false);
                setScanMessage(null);
                setSnackbarOpen(false);

                const cardId = serialNumber;

                if (!cardId) {
                    setScanMessage("ID card non rilevato o formattato in modo non valido.");
                    setScanSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }

                console.log(`Card ID letto: ${cardId}`);

                try {
                    const stakeholderResponse = await StakeholderService.get(cardId);
                    const stakeholder = stakeholderResponse.data;

                    if (!stakeholder) {
                        setScanMessage(`Stakeholder con ID ${cardId} non trovato nel sistema.`);
                        setScanSeverity('error');
                        setSnackbarOpen(true);
                        return;
                    }

                    const now = dayjs();
                    const newActionData = {
                        contatto_id: stakeholder.id,
                        data: now.format('YYYY-MM-DD'),
                        orario_inizio: (movementType === 'entrata') ? now.format('HH:mm') : null,
                        orario_fine: (movementType === 'uscita') ? now.format('HH:mm') : null,
                        tipologia: activityType,
                        note: `Registrazione ${movementType} tramite NFC`,
                        utente: 'NFC_Scanner',
                    };

                    const actionResponse = await ActionService.registerNFCActivity(newActionData);

                    let confirmationMessage = '';
                    if (movementType === 'entrata') {
                        confirmationMessage = `Entrata registrata per: ${stakeholder.nome} ${stakeholder.cognome || stakeholder.ragione_sociale} (Tipo: ${activityType}) alle ${newActionData.orario_inizio}.`;
                    } else {
                        confirmationMessage = `Uscita registrata per: ${stakeholder.nome} ${stakeholder.cognome || stakeholder.ragione_sociale} (Tipo: ${activityType}) alle ${newActionData.orario_fine}.`;
                    }
                    setScanMessage(confirmationMessage);
                    setScanSeverity('success');
                    setSnackbarOpen(true);

                } catch (dbError) {
                    console.error("Errore durante il recupero stakeholder o registrazione attività:", dbError);
                    let errorMessage = 'Si è verificato un errore durante l\'operazione.';
                    let severity = 'error';

                    if (dbError.response && dbError.response.data && dbError.response.data.message) {
                        errorMessage = dbError.response.data.message;
                        if (dbError.response.status === 409 || dbError.response.status === 404) {
                            severity = 'warning';
                        }
                    } else if (dbError.message) {
                        errorMessage = dbError.message;
                    }

                    setScanMessage(`Errore: ${errorMessage}`);
                    setScanSeverity(severity);
                    setSnackbarOpen(true);
                }
            };

            reader.onreadingerror = (event) => {
                setScanning(false);
                setScanMessage(null);
                setSnackbarOpen(false);
                setNfcError(`Errore di lettura NFC: ${event.message || 'Riprova.'}`);
                setScanSeverity('error');
                setSnackbarOpen(true);
            };

        } catch (error) {
            setScanning(false);
            setScanMessage(null);
            setSnackbarOpen(false);
            if (error.name === "NotAllowedError" || error.name === "SecurityError") {
                setNfcError("Accesso NFC negato. Assicurati che il browser abbia i permessi o che sia in HTTPS.");
            } else {
                setNfcError(`Impossibile avviare la scansione NFC: ${error.message || 'Errore sconosciuto.'}`);
            }
            setScanSeverity('error');
            setSnackbarOpen(true);
        }
    }, [movementType, activityType]);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const isScanButtonDisabled = scanning || !movementType || !activityType || !isWebNFCSupported;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#4CAF50', // Green background
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
            }}
        >
            {/* Header: Logo, Data e Orologio */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    zIndex: 1000,
                    flexWrap: 'wrap',
                }}
            >
                {/* Placeholder per il logo (sinistra) */}
                <Box>
                    <img src="/your_logo_path.png" alt="Logo Azienda" style={{ height: '60px', width: 'auto' }} />
                </Box>

                {/* Data e Orologio (destra) */}
                <Box sx={{ textAlign: 'right', color: 'white', ml: { xs: 0, sm: 2 } }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {currentDateFormatted}
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', fontSize: { xs: '2.5rem', sm: '3rem' }, lineHeight: 1.2 }}>
                        {currentTime}
                    </Typography>
                </Box>
            </Box>

            {/* Main Content Card */}
            <Paper elevation={8} sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                maxWidth: 500,
                width: '100%',
                borderRadius: '15px',
                boxShadow: '0px 10px 15px rgba(0,0,0,0.2)',
                mt: { xs: 12, sm: 15 },
                mb: 2,
                zIndex: 1,
            }}>
                <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Registrazione Attività tramite NFC
                </Typography>
                <Divider />

                {nfcError && (
                    <Alert severity="error" sx={{ mb: 1, boxShadow: 1 }}>
                        {nfcError}
                    </Alert>
                )}
                {!isWebNFCSupported && !nfcError && (
                    <Alert severity="warning" sx={{ mb: 1, boxShadow: 1 }}>
                        L'API Web NFC non è supportata dal tuo browser. Funziona al meglio su Chrome per Android.
                        Per testare, assicurati di essere su **HTTPS** (su localhost funziona anche HTTP).
                    </Alert>
                )}

                {/* Toggle Buttons for Entrata/Uscita */}
                <FormControl component="fieldset" fullWidth>
                    <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'center' }}>
                        Seleziona tipo di movimento:
                    </Typography>
                    <ToggleButtonGroup
                        value={movementType}
                        exclusive
                        onChange={(event, newMovementType) => {
                            if (newMovementType !== null) {
                                setMovementType(newMovementType);
                            }
                        }}
                        aria-label="tipo movimento"
                        sx={{ justifyContent: 'center', gap: 2 }}
                    >
                        {/* Stili per i bottoni "Entrata" e "Uscita" */}
                        <ToggleButton
                            value="entrata"
                            aria-label="entrata"
                            sx={{
                                flexGrow: 1,
                                py: 1.5,
                                // Colore di sfondo quando selezionato
                                '&.Mui-selected': {
                                    backgroundColor: '#4CAF50', // Verde Material-UI
                                    color: 'white', // Testo bianco
                                    '&:hover': {
                                        backgroundColor: '#4CAF50', // Mantieni verde anche all'hover
                                    },
                                },
                                // Colore del bordo quando selezionato
                                '&.Mui-selected.MuiToggleButton-root': {
                                    borderColor: '#4CAF50',
                                },
                                // Testo normale
                                color: 'rgba(0, 0, 0, 0.87)', // Colore testo predefinito
                                borderColor: 'rgba(0, 0, 0, 0.12)', // Colore bordo predefinito
                            }}
                        >
                            <Typography variant="h6">Entrata</Typography>
                        </ToggleButton>
                        <ToggleButton
                            value="uscita"
                            aria-label="uscita"
                            sx={{
                                flexGrow: 1,
                                py: 1.5,
                                // Colore di sfondo quando selezionato
                                '&.Mui-selected': {
                                    backgroundColor: '#4CAF50', // Verde Material-UI
                                    color: 'white', // Testo bianco
                                    '&:hover': {
                                        backgroundColor: '#4CAF50', // Mantieni verde anche all'hover
                                    },
                                },
                                // Colore del bordo quando selezionato
                                '&.Mui-selected.MuiToggleButton-root': {
                                    borderColor: '#4CAF50',
                                },
                                // Testo normale
                                color: 'rgba(0, 0, 0, 0.87)', // Colore testo predefinito
                                borderColor: 'rgba(0, 0, 0, 0.12)', // Colore bordo predefinito
                            }}
                        >
                            <Typography variant="h6">Uscita</Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>

                <FormControl fullWidth required sx={{ mt: 2 }}>
                    <InputLabel id="activity-type-label">Tipo di Attività</InputLabel>
                    <Select
                        labelId="activity-type-label"
                        id="activity-type-select"
                        value={activityType}
                        label="Tipo di Attività"
                        onChange={(e) => setActivityType(e.target.value)}
                        sx={{ '& .MuiSelect-select': { py: 1.5 } }}
                    >
                        <MenuItem value=""><em>Seleziona</em></MenuItem>
                        {tables.map((type) => (
                            <MenuItem key={type.id} value={type.campi}>
                                {type.campi}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={scanning ? <CircularProgress size={20} color="inherit" /> : <SpeakerNotesIcon />}
                    onClick={startNFCScan}
                    disabled={isScanButtonDisabled}
                    sx={{ mt: 3, py: 2, fontSize: '1.2rem', fontWeight: 'bold' }}
                >
                    {scanning ? "Scansione in corso..." : "Avvia Scansione NFC"}
                </Button>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={scanSeverity} sx={{ width: '100%' }}>
                        {scanMessage}
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
};

export default NFCScanner;